import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  Eye,
  CreditCard,
  TrendingUp
} from 'lucide-react';
import PaymentModal from './PaymentModal';
import PaymentHistoryModal from './PaymentHistoryModal';

const StudentFeeDashboard = ({ 
  enrollmentId, 
  academicYear, 
  feeApi, 
  formatCurrency,
  onPaymentSuccess 
}) => {
  const [feeRecords, setFeeRecords] = useState([]); // Changed to array
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [paymentMode, setPaymentMode] = useState('specific'); // 'specific' or 'waterfall'

  useEffect(() => {
    if (enrollmentId && academicYear) {
      loadFeeRecords();
    }
  }, [enrollmentId, academicYear]);

  const loadFeeRecords = async () => {
    setLoading(true);
    try {
      // Get ALL records for this enrollment
      const response = await feeApi.getAllRecords({ 
        academicYear: academicYear 
      });
      
      if (response?.success && response?.data) {
        // Filter records for this specific enrollment
        const studentRecords = response.data.filter(record => {
          const recEnrollmentId = record.studentEnrollmentId?._id || record.studentEnrollmentId?.id || record.studentEnrollmentId;
          return recEnrollmentId === enrollmentId;
        });

        // Sort by created date (newest first) and prioritize unpaid records
        const sortedRecords = studentRecords.sort((a, b) => {
          // First, prioritize unpaid records
          if (a.balance > 0 && b.balance === 0) return -1;
          if (a.balance === 0 && b.balance > 0) return 1;
          // Then by creation date (newest first)
          return new Date(b.createdAt) - new Date(a.createdAt);
        });

        setFeeRecords(sortedRecords);
        // Auto-select first record (should be unpaid if any exist)
        if (sortedRecords.length > 0) {
          setSelectedRecord(sortedRecords[0]);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (category) => {
    const due = category.amount - category.paid;
    const isOverdue = category.dueDate && new Date(category.dueDate) < new Date() && due > 0;
    
    if (category.status === 'Paid' || due <= 0) {
      return {
        icon: <CheckCircle2 className="h-5 w-5" />,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        label: 'Paid',
        emoji: '✅'
      };
    }
    
    if (isOverdue) {
      return {
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: 'Overdue',
        emoji: '🔴'
      };
    }
    
    if (category.paid > 0) {
      return {
        icon: <Clock className="h-5 w-5" />,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        label: 'Partial',
        emoji: '🟡'
      };
    }
    
    return {
      icon: <Clock className="h-5 w-5" />,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      label: 'Due',
      emoji: '⚪'
    };
  };

  const handlePayNow = (category) => {
    setSelectedCategory(category);
    setPaymentMode('specific');
    setShowPaymentModal(true);
  };

  const handleWaterfallPayment = () => {
    setSelectedCategory(null);
    setPaymentMode('waterfall');
    setShowPaymentModal(true);
  };

  const handleViewHistory = (category) => {
    setSelectedCategory(category);
    setShowHistoryModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    loadFeeRecords(); // Reload all records
    if (onPaymentSuccess) onPaymentSuccess();
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        Loading fee details...
      </div>
    );
  }

  if (!selectedRecord) {
    return (
      <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <p>No fee records found for this student</p>
      </div>
    );
  }

  const feeRecord = selectedRecord; // Use selected record
  const totalDue = feeRecord.balance || ((feeRecord.totalAmount || 0) - (feeRecord.totalPaid || 0));

  return (
    <div className="space-y-4">
      {/* Fee Record Selector - Show if multiple records */}
      {feeRecords.length > 1 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <p className="text-xs font-medium text-yellow-800 mb-1.5">
                Multiple fee records found. Showing {feeRecord.balance > 0 ? 'unpaid' : 'paid'} record.
              </p>
               <select
                value={feeRecords.indexOf(selectedRecord)}
                onChange={(e) => setSelectedRecord(feeRecords[parseInt(e.target.value)])}
                className="w-full max-w-md px-2 py-1.5 text-xs border border-yellow-300 rounded bg-white focus:ring-1 focus:ring-yellow-500"
              >
                {feeRecords.map((record, idx) => (
                  <option key={idx} value={idx}>
                    {record.feeStructureId?.name || 'No Structure'} - 
                    {record.balance > 0 ? ` ₹${(record.balance || 0).toLocaleString('en-IN')} Due` : ' Fully Paid'} - 
                    Created: {new Date(record.createdAt).toLocaleDateString('en-IN')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="bg-white p-3 rounded border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase">Total Fee</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">
                {formatCurrency(feeRecord.totalAmount)}
              </p>
            </div>
            <div className="p-1.5 rounded" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }}>
              <DollarSign className="h-4 w-4" style={{ color: 'var(--primary-color, #4f46e5)' }} />
            </div>
          </div>
        </div>

        <div className="bg-white p-3 rounded border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase">Paid</p>
              <p className="text-lg font-bold text-green-600 mt-0.5">
                {formatCurrency(feeRecord.totalPaid)}
              </p>
            </div>
            <div className="p-1.5 bg-green-50 rounded">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-3 rounded border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase">Balance</p>
              <p className="text-lg font-bold text-red-600 mt-0.5">
                {formatCurrency(totalDue)}
              </p>
            </div>
            <div className="p-1.5 bg-red-50 rounded">
              <TrendingUp className="h-4 w-4 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-3 rounded border border-indigo-200 shadow-sm">
          <button
            onClick={handleWaterfallPayment}
            disabled={totalDue <= 0}
            className="w-full h-full flex flex-col items-center justify-center gap-1 transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ '--hover-bg': 'color-mix(in srgb, var(--primary-color, #4f46e5) 5%, white)' }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 5%, white)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = ''}
          >
            <CreditCard className="h-5 w-5" style={{ color: 'var(--primary-color, #4f46e5)' }} />
            <span className="text-xs font-bold" style={{ color: 'var(--primary-color, #4f46e5)' }}>Make Payment</span>
          </button>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white rounded border border-gray-200 shadow-sm">
        <div className="p-3 border-b border-gray-200">
          <h3 className="text-sm font-bold text-gray-900">Category Breakdown</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Academic Year: {feeRecord.academicYear}
          </p>
        </div>

        <div className="p-3 space-y-3">
          {feeRecord.breakdown?.map((category, index) => {
            const statusConfig = getStatusConfig(category);
            const due = category.amount - category.paid;
            const isOverdue = category.dueDate && new Date(category.dueDate) < new Date() && due > 0;

            return (
              <div
                key={index}
                className={`border rounded p-3 transition-all hover:shadow-md ${statusConfig.borderColor}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded ${statusConfig.bgColor}`}>
                      {React.cloneElement(statusConfig.icon, { className: "h-4 w-4" })}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">
                        {statusConfig.emoji} {category.categoryName}
                      </h4>
                      <span className={`text-[10px] font-bold ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Amount Details */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <p className="text-[10px] text-gray-500 mb-0.5">Total Amount</p>
                    <p className="text-xs font-bold text-gray-900">
                      {formatCurrency(category.amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 mb-0.5">Paid</p>
                    <p className="text-xs font-bold text-green-600">
                      {formatCurrency(category.paid)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 mb-0.5">Due</p>
                    <p className="text-xs font-bold text-red-600">
                      {formatCurrency(due)}
                    </p>
                  </div>
                </div>

                {/* Due Date */}
                {category.dueDate && (
                  <div className="flex items-center gap-1.5 mb-3">
                    <Calendar className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-600">
                      Due: {new Date(category.dueDate).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                      {isOverdue && (
                        <span className="ml-1.5 text-[10px] font-bold text-red-600">
                          (Overdue!)
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                  {due > 0 && (
                    <button
                      onClick={() => handlePayNow(category)}
                      className="btn-primary flex-1 text-xs shadow-sm flex items-center justify-center gap-1.5"
                    >
                      <CreditCard className="h-3.5 w-3.5" />
                      Pay Now
                    </button>
                  )}
                  {category.paymentDetails && category.paymentDetails.length > 0 && (
                    <button
                      onClick={() => handleViewHistory(category)}
                      className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      History ({category.paymentDetails.length})
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          enrollmentId={feeRecord.studentEnrollmentId?._id || feeRecord.studentEnrollmentId?.id || enrollmentId}
          category={selectedCategory}
          paymentMode={paymentMode}
          feeRecord={feeRecord}
          feeApi={feeApi}
          formatCurrency={formatCurrency}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Payment History Modal */}
      {showHistoryModal && selectedCategory && (
        <PaymentHistoryModal
          category={selectedCategory}
          formatCurrency={formatCurrency}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
};

export default StudentFeeDashboard;
